import { isArray, isEmpty, isNumber, isObjectLike, pickBy } from 'lodash'
import { REGEXP_SPLIT_WORDS } from '@/common/constants/regExp'
import { FILTERS_BOOLEANS_TYPES } from '@/common/constants/filtersFields'
import {
  getCookie as getCookiesNext,
  setCookie as setCookiesNext,
} from 'cookies-next'
import { FALLBACK_LANG } from '@/common/constants/language'

export const getCookie = (cookies, name) => {
  const cookiesMap = new Map(
    cookies.split(';').map((cookie) => {
      const [name, ...rest] = cookie.trim().split('=')
      return [name, rest.join('=')]
    })
  )

  if (cookiesMap.has(name)) {
    return cookiesMap.get(name)
  } else {
    return null
  }
}

export const getCorrectValueFromField = (field) => {
  if (Array.isArray(field)) {
    return field.join(', ')
  }

  return field
}

export const formatNumber = (number, locale = 'en', withSuffix = true) => {
  const oneThousand = 1000
  const oneMillion = 1000000
  const oneBillion = 1000000000

  if (number === null || number === undefined || isNaN(number)) return '0'

  number = typeof number === 'string' ? parseFloat(number) : number

  const suffixes = {
    en: { K: ' K', M: ' MLN', B: ' BLN' },
    uk: { K: ' тис.', M: ' млн', B: ' млрд' },
    ru: { K: ' тыс.', M: ' млн', B: ' млрд' },
  }

  const localeSuffix = suffixes[locale] || suffixes['en']

  if (number >= oneBillion) {
    const formatted = (number / oneBillion).toLocaleString(locale, {
      maximumFractionDigits: 2,
    })
    return withSuffix ? formatted + localeSuffix.B : formatted
  }

  if (number >= oneMillion) {
    const formatted = (number / oneMillion).toLocaleString(locale, {
      maximumFractionDigits: 0,
    })
    return withSuffix ? formatted + localeSuffix.M : formatted
  }

  if (number >= oneThousand) {
    const formatted = (number / oneThousand).toLocaleString(locale, {
      maximumFractionDigits: 0,
    })
    return withSuffix ? formatted + localeSuffix.K : formatted
  }

  return number.toLocaleString(locale)
}
export const getFirstDataFromObject = (data) => {
  if (!data) {
    data = {}
  }

  const keys = Object.keys(data ?? [])
  return data[keys[0]] ? data[keys[0]] : {}
}

export const addScriptToBody = (id, src, callback) => {
  const script = document.createElement('script')
  script.id = id
  script.src = src
  script.async = true
  document.body.appendChild(script)

  script.addEventListener('load', callback)
}

export const delay = (timeout) => {
  return new Promise((res) => {
    setTimeout(() => {
      res()
    }, timeout)
  })
}

export const getDataWithoutEmptyValues = (data) => {
  if (!isObjectLike(data)) {
    return data || null
  }

  const filtered = pickBy(data, (value) => {
    if (isObjectLike(value) && isArray(value)) {
      const nestedData = getDataWithoutEmptyValues(value)
      return !isEmpty(nestedData)
    } else if (isNumber(value)) {
      return value > 0
    } else {
      return !(isEmpty(value) || (isArray(value) && isEmpty(value)))
    }
  })

  return !Object.keys(filtered).length ? null : filtered
}

export const addZeroPad = (value) => {
  return value ? String(value).padStart(2, '0') : ''
}

export const getFieldValueFromData = (data, field) => {
  if (!field) return

  const keys = field.split('.')
  return keys.reduce(
    (acc, key) => (acc && acc[key] !== 'undefined' ? acc[key] : undefined),
    data
  )
}

export const createBooleansMapFromString = (value) => {
  let prefixValue = ''
  const values = value?.match(REGEXP_SPLIT_WORDS)

  if (!values) return {}

  return values.reduce((acc, next) => {
    if (FILTERS_BOOLEANS_TYPES.includes(next)) {
      prefixValue += !prefixValue.length ? `${next} ` : ` ${next} `
      return acc
    }

    const value = `${prefixValue}${next}`
    acc[value] = value
    prefixValue = ''
    return acc
  }, {})
}

export const isContainsMultipleWords = (value, moreThan = 1) => {
  if (!value) return false

  const trimmedValue = value.trim()
  const words = trimmedValue.split(/\s+/)

  return words.length > moreThan
}

export const getOnlyFilteredWords = (str) => {
  if (!str) return []

  const words = str?.match(REGEXP_SPLIT_WORDS) ?? []
  return words.reduce((acc, next) => {
    if (FILTERS_BOOLEANS_TYPES.includes(next)) return acc

    acc.push(next)
    return acc
  }, [])
}

export const wrapWordsInSpan = (str = '', regexp = REGEXP_SPLIT_WORDS) => {
  let correctStr = str

  if (Array.isArray(str)) {
    correctStr = str.join(' ')
  }

  if (!correctStr)
    return {
      text: '',
      count: 0,
      list: [],
    }
  const words = correctStr.match(regexp)
  let result = ''
  const list = []
  let count = 0

  for (let i = 0; i < words.length; i++) {
    const word = words[i].trim()

    if (!list[count]) {
      list[count] = {
        text: '',
        value: '',
        valueWithModify: '',
      }
    }

    if (word.startsWith('"') || word.startsWith("'")) {
      result += `<span>${word}</span> `
      list[count].text += `<span>${word}</span>`
      list[count].valueWithModify += `${word} `
      list[count].value += `${word} `
      count++
    } else if (word === 'and' || word === 'not' || word === 'or') {
      result += word.toUpperCase() + ' '
      list[count].text += word.toUpperCase() + ' '
      list[count].valueWithModify += word + ' '
    } else {
      result += `<span>${word}</span>`
      list[count].text += `<span>${word}</span>`
      list[count].valueWithModify += word
      list[count].value += word
      count++
    }
  }

  return { text: result.trim(), count, list }
}

export const getCookiesToState = (slice) => {
  return `${slice}State`
}

export const getPersistCookieNext = (key, ctx) => {
  const isServer = !!ctx

  const value = isServer
    ? getCookiesNext(key, { req: ctx.req, res: ctx.res })
    : getCookiesNext(key)
  return value ? JSON.parse(value) : null
}

export const setPersistCookie = async (key, value = {}, ctx = {}) => {
  const isServer = !!ctx?.req && !!ctx?.res
  const serializedValue = JSON.stringify(value)

  if (ctx && ctx?.res?.headersSent) return

  if (isServer) {
    setCookiesNext(key, serializedValue, {
      req: ctx.req,
      res: ctx.res,
      ...ctx,
    })
  } else {
    setCookiesNext(key, serializedValue)
  }
}

export const getInitialStateBySlices = (slices, context) => {
  return slices.reduce((acc, next) => {
    const cookieState = getPersistCookieNext(getCookiesToState(next), context)

    if (cookieState) {
      acc[next] = cookieState
    }

    return acc
  }, {})
}

export const getFallbackLangByLocale = (locale) => {
  return locale === 'es' ? FALLBACK_LANG : locale
}

export const popupCenter = (url) => {
  const dualScreenLeft = window.screenLeft ?? window.screenX
  const dualScreenTop = window.screenTop ?? window.screenY

  const width =
    window.innerWidth ?? document.documentElement.clientWidth ?? screen.width

  const height =
    window.innerHeight ?? document.documentElement.clientHeight ?? screen.height

  const systemZoom = width / window.screen.availWidth

  const left = (width - 500) / 2 / systemZoom + dualScreenLeft
  const top = (height - 550) / 2 / systemZoom + dualScreenTop

  const newWindow = window.open(
    url,
    `width=${500 / systemZoom},height=${
      550 / systemZoom
    },top=${top},left=${left}`
  )

  newWindow?.focus()
}
